if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.isPremiumCarousel = this.classList.contains('media-gallery--premium-carousel');
        this.swipeGalleryOnly = this.classList.contains('media-gallery--swipe-gallery');
        this.isPdpStack = this.classList.contains('media-gallery--pdp-stack');
        this.stackScrollbar = this.querySelector('[data-pdp-stack-scrollbar]');
        this.stackThumbnails = this.querySelector('[data-pdp-stack-thumbnails]');

        if (!this.elements.viewer) return;

        if (!this.elements.thumbnails && !this.isPremiumCarousel && !this.isPdpStack) return;

        if (!this.isPdpStack) {
          this.elements.viewer.addEventListener(
            'slideChanged',
            debounce(this.onSlideChanged.bind(this), this.isPremiumCarousel ? 120 : 500)
          );
        }

        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch.querySelector('button').addEventListener(
              'click',
              this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false)
            );
          });
        }

        if (
          this.dataset.desktopLayout &&
          this.dataset.desktopLayout.includes('thumbnail') &&
          this.mql.matches &&
          !this.swipeGalleryOnly &&
          !this.isPdpStack
        )
          this.removeListSemantic();

        if (this.isPdpStack) {
          this.initPdpStackScrollbar();
          this.initPdpStackThumbnails();
        }
      }

      disconnectedCallback() {
        if (this.boundUpdatePdpStackScrollbar) {
          window.removeEventListener('scroll', this.boundUpdatePdpStackScrollbar);
          window.removeEventListener('resize', this.boundUpdatePdpStackScrollbar);
        }
        if (this.stackThumbViewport && this.boundUpdatePdpStackThumbnailArrows) {
          this.stackThumbViewport.removeEventListener('scroll', this.boundUpdatePdpStackThumbnailArrows);
        }
        if (this.boundUpdatePdpStackActiveThumbnail) {
          window.removeEventListener('scroll', this.boundUpdatePdpStackActiveThumbnail);
          window.removeEventListener('resize', this.boundUpdatePdpStackActiveThumbnail);
        }
      }

      onSlideChanged(event) {
        if (this.isPdpStack) return;
        const current = event.detail.currentElement;
        if (!current || !current.dataset) return;

        if (this.isPremiumCarousel && current.dataset.mediaId) {
          this.elements.viewer.querySelectorAll('[data-media-id]').forEach((el) => {
            el.classList.remove('is-active');
            if (!this.swipeGalleryOnly) el.setAttribute('hidden', '');
          });
          current.classList.add('is-active');
          if (!this.swipeGalleryOnly) current.removeAttribute('hidden');
        }

        if (!this.elements.thumbnails) return;

        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${current.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        if (!activeMedia) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
          if (this.isPremiumCarousel && !this.swipeGalleryOnly) element.setAttribute('hidden', '');
        });
        activeMedia.classList.add('is-active');
        if (this.isPremiumCarousel && !this.swipeGalleryOnly) activeMedia.removeAttribute('hidden');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail) activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider && typeof this.elements.viewer.resetPages === 'function') {
            this.elements.viewer.resetPages();
          }
        }

        this.preventStickyHeader();

        window.setTimeout(() => {
          const list = activeMedia.parentElement;
          if (this.isPdpStack) {
            activeMedia.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          } else if (list && (this.elements.thumbnails || this.isPremiumCarousel)) {
            list.scrollTo({ left: activeMedia.offsetLeft });
          }
        });

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) {
          const slides = Array.from(this.elements.viewer.querySelectorAll('[data-media-id]'));
          const position = slides.indexOf(activeMedia) + 1;
          if (position > 0) this.announceLiveRegion(activeMedia, String(position));
          return;
        }

        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (!activeThumbnail || !activeThumbnail.dataset) return;

        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        const btn = thumbnail.querySelector('button');
        if (btn) btn.setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      initPdpStackScrollbar() {
        if (!this.stackScrollbar) return;

        this.stackScrollList = this.querySelector('.pdp-stack-media-list');
        this.stackScrollTrack = this.querySelector('[data-pdp-stack-scroll-track]');
        this.stackScrollThumb = this.querySelector('[data-pdp-stack-scroll-thumb]');
        this.stackScrollPrev = this.querySelector('[data-pdp-stack-scroll-prev]');
        this.stackScrollNext = this.querySelector('[data-pdp-stack-scroll-next]');
        this.stackScrollItems = Array.from(this.querySelectorAll('.pdp-stack-media-list > [data-media-id]'));

        if (!this.stackScrollList || !this.stackScrollTrack || !this.stackScrollThumb || this.stackScrollItems.length <= 1) {
          this.stackScrollbar.hidden = true;
          return;
        }

        this.boundUpdatePdpStackScrollbar = this.updatePdpStackScrollbar.bind(this);
        this.stackScrollPrev?.addEventListener('click', () => this.scrollPdpStackBy(-1));
        this.stackScrollNext?.addEventListener('click', () => this.scrollPdpStackBy(1));
        window.addEventListener('scroll', this.boundUpdatePdpStackScrollbar, { passive: true });
        window.addEventListener('resize', this.boundUpdatePdpStackScrollbar);
        this.boundUpdatePdpStackScrollbar();
      }

      initPdpStackThumbnails() {
        if (!this.stackThumbnails) return;

        this.stackScrollItems = this.stackScrollItems || Array.from(this.querySelectorAll('.pdp-stack-media-list > [data-media-id]'));
        this.stackThumbViewport = this.querySelector('[data-pdp-stack-thumbnails-viewport]');
        this.stackThumbButtons = Array.from(this.querySelectorAll('[data-pdp-stack-thumbnail]'));
        this.stackThumbPrev = this.querySelector('[data-pdp-stack-thumbnails-prev]');
        this.stackThumbNext = this.querySelector('[data-pdp-stack-thumbnails-next]');

        if (!this.stackThumbViewport || this.stackThumbButtons.length <= 1) {
          this.stackThumbnails.hidden = true;
          return;
        }

        this.stackThumbPrev?.addEventListener('click', () => this.scrollPdpStackThumbnailsBy(-1));
        this.stackThumbNext?.addEventListener('click', () => this.scrollPdpStackThumbnailsBy(1));
        this.boundUpdatePdpStackThumbnailArrows = this.updatePdpStackThumbnailArrows.bind(this);
        this.boundUpdatePdpStackActiveThumbnail = () => {
          this.updatePdpStackActiveThumbnail();
          this.updatePdpStackThumbnailArrows();
        };
        this.initPdpStackThumbnailDrag();
        this.stackThumbViewport.addEventListener('scroll', this.boundUpdatePdpStackThumbnailArrows, { passive: true });
        window.addEventListener('scroll', this.boundUpdatePdpStackActiveThumbnail, { passive: true });
        window.addEventListener('resize', this.boundUpdatePdpStackActiveThumbnail);
        this.updatePdpStackActiveThumbnail();
        this.updatePdpStackThumbnailArrows();
      }

      initPdpStackThumbnailDrag() {
        if (!this.stackThumbViewport) return;

        let isDragging = false;
        let startX = 0;
        let startScrollLeft = 0;

        this.stackThumbViewport.addEventListener('pointerdown', (event) => {
          if (event.button !== undefined && event.button !== 0) return;
          isDragging = true;
          startX = event.clientX;
          startScrollLeft = this.stackThumbViewport.scrollLeft;
          this.stackThumbViewport.classList.add('is-dragging');
          this.stackThumbViewport.setPointerCapture?.(event.pointerId);
        });

        this.stackThumbViewport.addEventListener('pointermove', (event) => {
          if (!isDragging) return;
          event.preventDefault();
          const distance = event.clientX - startX;
          this.stackThumbViewport.scrollLeft = startScrollLeft - distance;
        });

        const stopDragging = (event) => {
          if (!isDragging) return;
          isDragging = false;
          this.stackThumbViewport.classList.remove('is-dragging');
          this.stackThumbViewport.releasePointerCapture?.(event.pointerId);
        };

        this.stackThumbViewport.addEventListener('pointerup', stopDragging);
        this.stackThumbViewport.addEventListener('pointercancel', stopDragging);
        this.stackThumbViewport.addEventListener('pointerleave', stopDragging);
      }

      getPdpStackActiveIndex() {
        if (!this.stackScrollItems?.length) return 0;

        const targetY = window.innerHeight * 0.48;
        let activeIndex = 0;
        let closestDistance = Number.POSITIVE_INFINITY;

        this.stackScrollItems.forEach((item, index) => {
          const rect = item.getBoundingClientRect();
          const itemCenter = rect.top + rect.height / 2;
          const distance = Math.abs(itemCenter - targetY);
          if (distance < closestDistance) {
            closestDistance = distance;
            activeIndex = index;
          }
        });

        return activeIndex;
      }

      updatePdpStackScrollbar() {
        if (!this.stackScrollbar || !this.stackScrollList || !this.stackScrollTrack || !this.stackScrollThumb) return;

        if (!this.mql.matches || this.stackScrollItems.length <= 1) {
          this.stackScrollbar.hidden = true;
          return;
        }

        this.stackScrollbar.hidden = false;

        const listRect = this.stackScrollList.getBoundingClientRect();
        const listTop = listRect.top + window.scrollY;
        const trackHeight = this.stackScrollTrack.clientHeight;
        const trackInset = 13;
        const usableTrack = Math.max(trackHeight - trackInset * 2, 1);
        const visibleRatio = Math.min(window.innerHeight / Math.max(listRect.height, 1), 1);
        const thumbHeight = Math.max(24, Math.min(usableTrack, usableTrack * visibleRatio));
        const documentMaxScroll = Math.max(
          document.documentElement.scrollHeight - window.innerHeight,
          document.body.scrollHeight - window.innerHeight,
          0
        );
        const stackScrollEnd = Math.min(listTop + listRect.height - window.innerHeight, documentMaxScroll);
        const scrollRange = Math.max(stackScrollEnd - listTop, 1);
        const progress = Math.max(0, Math.min(1, (window.scrollY - listTop) / scrollRange));
        const thumbY = (usableTrack - thumbHeight) * progress;
        const activeIndex = this.getPdpStackActiveIndex();

        this.stackScrollThumb.style.height = `${thumbHeight}px`;
        this.stackScrollThumb.style.setProperty('--pdp-stack-thumb-y', `${thumbY}px`);

        if (this.stackScrollPrev) this.stackScrollPrev.disabled = activeIndex <= 0;
        if (this.stackScrollNext) this.stackScrollNext.disabled = activeIndex >= this.stackScrollItems.length - 1;
        this.updatePdpStackActiveThumbnail(activeIndex);
      }

      updatePdpStackActiveThumbnail(activeIndex = this.getPdpStackActiveIndex()) {
        if (!this.stackThumbButtons?.length || !this.stackScrollItems?.length) return;

        const activeItem = this.stackScrollItems[activeIndex];
        const mediaId = activeItem?.dataset?.mediaId;
        if (!mediaId) return;

        this.stackThumbButtons.forEach((button) => {
          const isActive = button.dataset.target === mediaId;
          button.classList.toggle('is-active', isActive);
          if (isActive) button.setAttribute('aria-current', 'true');
          else button.removeAttribute('aria-current');
        });

        const activeThumb = this.stackThumbButtons.find((button) => button.dataset.target === mediaId);
        if (!activeThumb || !this.stackThumbViewport) return;

        const viewportRect = this.stackThumbViewport.getBoundingClientRect();
        const thumbRect = activeThumb.getBoundingClientRect();
        const isVisible = thumbRect.left >= viewportRect.left + 8 && thumbRect.right <= viewportRect.right - 8;

        if (!isVisible) {
          const targetLeft =
            activeThumb.offsetLeft - this.stackThumbViewport.clientWidth / 2 + activeThumb.clientWidth / 2;
          this.stackThumbViewport.scrollTo({ left: targetLeft, behavior: 'smooth' });
        }
      }

      updatePdpStackThumbnailArrows() {
        if (!this.stackThumbViewport) return;
        const maxScroll = Math.max(this.stackThumbViewport.scrollWidth - this.stackThumbViewport.clientWidth, 0);
        if (this.stackThumbPrev) this.stackThumbPrev.disabled = this.stackThumbViewport.scrollLeft <= 2;
        if (this.stackThumbNext) this.stackThumbNext.disabled = this.stackThumbViewport.scrollLeft >= maxScroll - 2;
      }

      scrollPdpStackThumbnailsBy(direction) {
        if (!this.stackThumbViewport) return;
        const distance = Math.max(this.stackThumbViewport.clientWidth * 0.72, 160);
        this.stackThumbViewport.scrollBy({ left: distance * direction, behavior: 'smooth' });
      }

      scrollPdpStackToMedia(mediaId) {
        if (!mediaId) return;
        const target = this.querySelector(`.pdp-stack-media-list > [data-media-id="${mediaId}"]`);
        if (!target) return;

        this.querySelectorAll('.pdp-stack-media-list > [data-media-id]').forEach((item) =>
          item.classList.toggle('is-active', item === target)
        );
        this.preventStickyHeader();
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        window.setTimeout(() => {
          const index = this.stackScrollItems?.indexOf(target) ?? -1;
          if (index >= 0) this.updatePdpStackActiveThumbnail(index);
        }, 120);
      }

      scrollPdpStackBy(direction) {
        if (!this.stackScrollItems?.length) return;

        const activeIndex = this.getPdpStackActiveIndex();
        const targetIndex = Math.max(0, Math.min(this.stackScrollItems.length - 1, activeIndex + direction));
        const target = this.stackScrollItems[targetIndex];
        if (!target) return;

        this.preventStickyHeader();
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  );
}
